import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = "/images/logo.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'The Stay', path: '/stay' },
  { name: 'Experiences', path: '/experiences' },
  { name: 'Dining', path: '/dining' },
  { name: 'Gallery', path: '/gallery' },
  { name: 'About', path: '/about' },
  { name: 'Contact', path: '/contact' },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-void/90 backdrop-blur-md border-b border-gold-500/10">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0" data-testid="nav-logo">
            <img src={LOGO_URL} alt="Rudragarh" className="h-14 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className="nav-link relative text-sm uppercase tracking-widest text-foreground/80 hover:text-gold-500 transition-colors font-body"
                data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden lg:flex items-center space-x-4 xl:space-x-6">
            {user ? (
              <div className="flex items-center space-x-4">
                <Link
                  to={isAdmin ? '/admin' : '/dashboard'}
                  className="flex items-center space-x-2 text-foreground/80 hover:text-gold-500 transition-colors"
                  data-testid="nav-dashboard"
                >
                  <User size={18} />
                  <span className="text-sm uppercase tracking-wider">{user.name}</span>
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-foreground/60 hover:text-gold-500 transition-colors"
                  data-testid="nav-logout"
                >
                  <LogOut size={18} />
                </button>
              </div>
            ) : (
              <Link
                to="/auth"
                className="text-sm uppercase tracking-widest text-foreground/80 hover:text-gold-500 transition-colors"
                data-testid="nav-login"
              >
                Login
              </Link>
            )}
            <Link
              to="/book"
              className="btn-primary"
              data-testid="nav-book-now"
            >
              Book Now
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden text-foreground"
            data-testid="nav-mobile-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-void-paper border-t border-gold-500/10"
          >
            <div className="px-6 py-6 space-y-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={() => setIsOpen(false)}
                  className="block text-sm uppercase tracking-widest text-foreground/80 hover:text-gold-500 transition-colors py-2"
                >
                  {link.name}
                </Link>
              ))}
              <div className="pt-4 border-t border-gold-500/10 space-y-4">
                {user ? (
                  <>
                    <Link
                      to={isAdmin ? '/admin' : '/dashboard'}
                      onClick={() => setIsOpen(false)}
                      className="block text-sm uppercase tracking-widest text-foreground/80"
                    >
                      Dashboard
                    </Link>
                    <button
                      onClick={() => { handleLogout(); setIsOpen(false); }}
                      className="block text-sm uppercase tracking-widest text-foreground/60"
                    >
                      Logout
                    </button>
                  </>
                ) : (
                  <Link
                    to="/auth"
                    onClick={() => setIsOpen(false)}
                    className="block text-sm uppercase tracking-widest text-foreground/80"
                  >
                    Login
                  </Link>
                )}
                <Link
                  to="/book"
                  onClick={() => setIsOpen(false)}
                  className="btn-primary block text-center"
                >
                  Book Now
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
